import React, { FC } from "react";

import { KpkFolder } from "@keepeek/commons";
import { BaseComponentProps, KFolderBreadcrumb } from "@keepeek/refront-components";
import * as Sentry from "@sentry/nextjs";

import { KIcon } from "../../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { Breadcrumb } from "../../../models/configuration/components/breadcrumb";
import { FolderBrowse } from "../../../models/configuration/components/folderBrowse";
import { useActiveFiltersFromURL } from "../../../providers/businessFilter/hooks/useActiveFiltersFromURL";
import { useConfiguration } from "../../../providers/config/hooks";
import { useInitConfig } from "../../../providers/config/hooks/init";
import { useImageOptim } from "../../../providers/config/hooks/useImageOptim";
import useFrontEditLabelWrapper from "../../../providers/frontEdit/hooks/useFrontEditLabelWrapper";
import ImageOptim from "../../ImageOptim";
import { KWidgetProps } from "../models";

export type FolderBreadcrumbProps = {
  folderId: number;
  home?: () => void;
  initFolderRootId?: KpkFolder["id"];
} & KWidgetProps &
  BaseComponentProps;

const FolderBreadcrumb: FC<FolderBreadcrumbProps> = function FolderBreadcrumb({
  folderId,
  className,
  initFolderRootId,
  home,
}) {
  const { initConfig } = useInitConfig();
  const { setActiveFilters } = useActiveFiltersFromURL();
  const sentryCallback = ({ category, message }) => Sentry.addBreadcrumb({ category, message });

  const { breadcrumbRootFolderId, changeDataViewOnClick = false } = useConfiguration<Breadcrumb>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_BREADCRUMB,
  ) ?? { breadcrumbRootFolderId: initFolderRootId, changeDataViewOnClick: false };
  const {
    showFolderCover = false,
    showSubFolderLink = false,
    showMainActionLink = true,
    showFolderOnArrival = false,
  } = useConfiguration<FolderBrowse>(CONFIGURATION_SECTION_PATH.COMPONENTS_FOLDER_BROWSE) ?? {
    showFolderCover: false,
    showSubFolderLink: false,
    showMainActionLink: true,
    showFolderOnArrival: false,
  };

  const { FrontEditComponentsLabelWrapper } = useFrontEditLabelWrapper();

  const { loading, shouldOptimize } = useImageOptim();

  return (
    <KFolderBreadcrumb
      foldersAuthorizedId={[]}
      className={className}
      folderId={folderId}
      home={home}
      initFolderRootId={initFolderRootId}
      clientIp={initConfig?.clientIp}
      showFolderCover={showFolderCover}
      showSubFolderLink={showSubFolderLink}
      showMainActionLink={showMainActionLink}
      showFolderOnArrival={showFolderOnArrival}
      breadcrumbRootFolderId={breadcrumbRootFolderId}
      changeDataViewOnClick={changeDataViewOnClick}
      loadingImageOptim={loading}
      shouldOptimizeImage={shouldOptimize}
      setActiveFilters={setActiveFilters}
      KIcon={KIcon}
      EditableSanitizeHtml={FrontEditComponentsLabelWrapper}
      ImageOptim={(src, className, alt, width, height, children) => (
        <ImageOptim src={src} className={className} alt={alt} width={width} height={height}>
          {children}
        </ImageOptim>
      )}
      sentryCallback={sentryCallback}
    />
  );
};

export default FolderBreadcrumb;
